import { AuthContext } from '@core/shared';
import { AccountLinkCodeStatus, OptionsApi } from '@core/swagger-client';
import { AccountVerificationRequired, Button, ContainerDefault, ErrorMessage, ErrorMessagePage, Heading, LoaderPage } from '@core/ui';
import { getRouteApi, useNavigate } from '@online-webinar/router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useContext } from 'react';
import { Trans, useTranslation } from 'react-i18next';

const route = getRouteApi('/account-manager-invite/$inviteCode');
export const AccountManagerInvitePage = () => {
    const { inviteCode } = route.useParams();
    const { t } = useTranslation();
    const inviteData = useQuery(
        OptionsApi.accountManagement.getInviteInformation(inviteCode!, {
            refetchOnReconnect: false,
            refetchOnMount: false
        })
    );
    const { refreshToken } = useContext(AuthContext);
    const navigate = useNavigate();

    const { data: isVerified, error: isVerifiedError, isLoading: isVerifiedLoading } = useQuery(OptionsApi.account.isVerified());

    const acceptInviteMutation = useMutation(OptionsApi.accountManagement.acceptInvite());
    const acceptInvite = () => {
        acceptInviteMutation.mutate(inviteCode!, {
            onSuccess: () => {
                refreshToken().then(() => {
                    navigate({ to: '/switch-account' });
                });
            }
        });
    };
    const rejectInviteMutation = useMutation(OptionsApi.accountManagement.rejectInvite());
    const rejectInvite = () => {
        rejectInviteMutation.mutate(inviteCode!, {
            onSuccess: () => {
                refreshToken().then(() => {
                    navigate({ to: '/' });
                });
            }
        });
    };

    const error = inviteData.error ?? isVerifiedError;
    if (error) {
        return <ErrorMessagePage apiError={error} />;
    }

    if (inviteData.isLoading || isVerifiedLoading) {
        return <LoaderPage />;
    }

    let name = inviteData.data!.account.profile.nickname;
    if (inviteData.data!.account.profile.firstName && inviteData.data!.account.profile.lastName) {
        name = inviteData.data!.account.profile.firstName + ' ' + inviteData.data!.account.profile.lastName;
    }
    if (inviteData.data!.account.profile.companyName) {
        name = t('AccountManagerInvite.InviteBody.CombineNameAndCompanyName', { name, companyName: inviteData.data!.account.profile.companyName });
    }

    const mutationError = acceptInviteMutation.error ?? rejectInviteMutation.error;

    return (
        <ContainerDefault>
            {mutationError && <ErrorMessage apiError={mutationError} />}
            <Heading variant="h1" classNames="mb-4">
                <Trans i18nKey="AccountManagerInvite.PageHeading" values={{ account: inviteData.data!.account.profile.nickname }} />
            </Heading>
            <div className="max-w-4xl">
                {!isVerified && <AccountVerificationRequired />}
                {isVerified && (
                    <>
                        {inviteData.data!.isValid && inviteData.data!.status === AccountLinkCodeStatus.Valid && (
                            <>
                                <p className="my-4">
                                    <Trans
                                        i18nKey={isVerified ? 'AccountManagerInvite.InviteBodyVerified' : 'AccountManagerInvite.InviteBody'}
                                        values={{ name: name }}
                                    />
                                </p>
                                <div className="flex gap-4 flex-wrap">
                                    <Button
                                        variant="primary-outline"
                                        onClick={rejectInvite}
                                        disabled={!isVerified}
                                        isLoading={acceptInviteMutation.isPending || rejectInviteMutation.isPending}
                                    >
                                        {t('AccountManagerInvite.Button.RejectInvite')}
                                    </Button>
                                    <Button
                                        variant="primary"
                                        onClick={acceptInvite}
                                        disabled={!isVerified}
                                        isLoading={acceptInviteMutation.isPending || rejectInviteMutation.isPending}
                                    >
                                        {t('AccountManagerInvite.Button.AcceptInvite')}
                                    </Button>
                                </div>
                            </>
                        )}
                        {inviteData.data!.status === AccountLinkCodeStatus.IsUsed && (
                            <p className="mt-4">
                                <Trans i18nKey="AccountManagerInvite.InviteUsed" />
                            </p>
                        )}
                        {inviteData.data!.status === AccountLinkCodeStatus.Expired && (
                            <p className="mt-4">
                                <Trans i18nKey="AccountManagerInvite.InviteExpired" values={{ name: name }}>
                                    This key includes a
                                    <a href={`mailto:${inviteData.data!.account.profile.emailAddress}`} className="link">
                                        link
                                    </a>
                                </Trans>
                            </p>
                        )}
                        {inviteData.data!.status === AccountLinkCodeStatus.Invalid && (
                            <p className="mt-4">
                                <Trans i18nKey="AccountManagerInvite.Invalid" />
                            </p>
                        )}
                    </>
                )}
            </div>
        </ContainerDefault>
    );
};
